import type { LabChartTypeEnum } from "@eu.mdotm.api/portfolio-lab";
import { zExt } from "$root/third-party-integrations/zod";
import { zAgGridColumnState } from "$root/ui-lib/table/col-state-schema";
import type { ColumnState } from "ag-grid-community";
import z from "zod";
import type { PortfolioLabChartItem } from "../context";

// --- Persisted/wire preference schema ---

const zPerformancePreference = z.object({
	type: z.literal("PERFORMANCE"),
	columns: z.array(zAgGridColumnState),
	// Optional for backward compatibility with preferences persisted before the toggle existed.
	showDelta: z.boolean().optional(),
});
const zExposurePreference = z.object({
	type: z.literal("EXPOSURE"),
	firstLevel: z.string().nullable(),
	secondLevel: z.string().nullable(),
});
const zTablePreference = z.object({
	type: z.literal("TABLE"),
	columns: z.array(zAgGridColumnState),
	// Optional for backward compatibility with preferences persisted before the toggle existed.
	showDelta: z.boolean().optional(),
});
const zChartPreference = z.discriminatedUnion("type", [zPerformancePreference, zExposurePreference, zTablePreference]);

/** Persisted/wire preference shape — carries a `type` discriminant for Zod. Context preferences are type-less. */
type WireChartPreference = z.infer<typeof zChartPreference>;

const labChartPreferencesSchema = zExt.versioned(
	1,
	z.object({ visible: z.boolean(), preference: zChartPreference.optional() }),
);

export function parseChartPreferences(prefs: unknown): { visible: boolean; preference?: WireChartPreference } {
	const result = labChartPreferencesSchema.safeParse(prefs);
	if (!result.success) {
		return { visible: true };
	}
	return result.data.data;
}

// --- Wire ↔ context chart item mapping ---

/**
 * Builds a discriminated {@link PortfolioLabChartItem} from a chart type and an optional parsed wire preference.
 * Drops the wire `type` discriminant — the context preference is type-less, discriminated by the outer `type`.
 * Falls back to per-type defaults when the parsed preference is absent or does not match `chartType`.
 *
 * `series` (the persisted `LabChartConfig.series` UUIDs) is the data dimension. For TABLE charts it dictates
 * membership; display order/visibility come from the persisted visual `ColumnState`, reconciled by `colId`.
 */
export function toChartItem(args: {
	uuid: string;
	name: string;
	visible: boolean;
	type: LabChartTypeEnum;
	preference?: WireChartPreference;
	series?: string[];
}): PortfolioLabChartItem {
	const { uuid, name, visible, type, preference, series } = args;
	const base = { uuid, name, visible, series: series ?? [] };
	switch (type) {
		case "PERFORMANCE":
			return {
				...base,
				type,
				preference:
					preference?.type === "PERFORMANCE"
						? { columns: preference.columns, showDelta: preference.showDelta ?? false }
						: { columns: [], showDelta: false },
			};
		case "EXPOSURE":
			return {
				...base,
				type,
				preference:
					preference?.type === "EXPOSURE"
						? { firstLevel: preference.firstLevel, secondLevel: preference.secondLevel }
						: { firstLevel: null, secondLevel: null },
			};
		case "TABLE": {
			const seriesSet = new Set(base.series);
			const visualColumns = preference?.type === "TABLE" ? preference.columns : [];
			// Keep persisted visual columns still in the series (preserves saved order/hide/width)…
			const orderedVisual = visualColumns.filter((c) => seriesSet.has(c.colId));
			// …then append any series concept lacking a visual column (e.g. freshly seeded), default-visible.
			const seen = new Set(orderedVisual.map((c) => c.colId));
			const appended: ColumnState[] = base.series.filter((colId) => !seen.has(colId)).map((colId) => ({ colId }));
			const columns: ColumnState[] = [...orderedVisual, ...appended].map((c) => ({ ...c, hide: c.hide ?? false }));
			const showDelta = (preference?.type === "TABLE" ? preference.showDelta : undefined) ?? false;
			return { ...base, type, preference: { columns, showDelta } };
		}
	}
}

/** Reconstructs the persisted/wire preference (with `type` discriminant) from a context chart item. */
export function toWirePreference(chart: PortfolioLabChartItem): WireChartPreference {
	switch (chart.type) {
		case "PERFORMANCE":
			return { type: "PERFORMANCE", columns: chart.preference.columns, showDelta: chart.preference.showDelta };
		case "EXPOSURE":
			return { type: "EXPOSURE", firstLevel: chart.preference.firstLevel, secondLevel: chart.preference.secondLevel };
		case "TABLE":
			return { type: "TABLE", columns: chart.preference.columns, showDelta: chart.preference.showDelta };
	}
}

export function defaultChartName(type: LabChartTypeEnum): string {
	switch (type) {
		case "PERFORMANCE":
			return "Performance";
		case "EXPOSURE":
			return "Exposure";
		case "TABLE":
			return "Portfolio Metrics";
	}
}

export const DEFAULT_LAB_CHARTS: PortfolioLabChartItem[] = [
	toChartItem({ uuid: crypto.randomUUID(), name: "Performance", visible: true, type: "PERFORMANCE" }),
	toChartItem({ uuid: crypto.randomUUID(), name: "Exposure", visible: true, type: "EXPOSURE" }),
	toChartItem({ uuid: crypto.randomUUID(), name: "Portfolio Metrics", visible: true, type: "TABLE" }),
];
